import type {DocumentManager} from '@wix/document-manager-core'
import type {
    CompStructure,
    Pointer,
    CompRef,
    PossibleViewModes,
    PS,
    RoutersDefinition
} from '@wix/document-services-types'
import {HooksExtensionApi, HOOKS as extensionHooks} from '@wix/document-manager-extensions'
import type {InnerElementDataUpdateBefore} from '@wix/document-manager-extensions/src/extensions/dataModel/hooks'
import type {AppManifestUpdatedEvent} from '@wix/document-manager-extensions/dist/src/extensions/builderComponents/builderComponentsHooks'
type DsHooks = typeof import('@wix/document-services-implementation').hooks

const notifyBeforeAddHookToExtensions = (
    ps: PS,
    componentToAddRef: Pointer,
    containerReference: Pointer,
    componentDefinition: CompStructure,
    optionalCustomId?: string,
    optionalIndex?: number
) => {
    ps.events.emitter.emit(
        ps.events.COMPONENTS.BEFORE_ADD_ROOT,
        componentToAddRef,
        containerReference,
        componentDefinition,
        optionalCustomId,
        optionalIndex
    )
}

const notifyGlobalWixThemeVariableValueWasChanged = (
    ps: PS,
    data: {cssVarName: string; type: string; value: string}
) => {
    ps.events.emitter.emit(ps.events.GLOBAL_VARIABLES.WIX_THEME_VAR_WAS_CHANGED, data)
}

const notifyExtensionsAfterRootAdd = (
    ps: PS,
    compToAddPointer: Pointer,
    containerPointer: Pointer,
    componentDefinition: CompStructure,
    viewMode?: PossibleViewModes
) => {
    const {hooks} = ps.extensionAPI as HooksExtensionApi
    hooks.executeHook(
        extensionHooks.COMPONENTS_HOOKS.ADD_ROOT.AFTER.createEvent({
            componentType: componentDefinition.componentType,
            compToAddPointer,
            componentDefinition,
            containerPointer,
            viewMode
        }),
        componentDefinition.componentType
    )
}

const notifyExtensionsAfterAdd = (
    ps: PS,
    compToAddPointer: Pointer,
    componentDefinition: CompStructure,
    optionalCustomId: string | undefined,
    mappers: Record<string, any>,
    containerPointer: Pointer
) => {
    const {hooks} = ps.extensionAPI as HooksExtensionApi
    hooks.executeHook(
        extensionHooks.COMPONENTS_HOOKS.ADD_COMPONENT.AFTER.createEvent({
            componentType: componentDefinition.componentType,
            compToAddPointer,
            componentDefinition,
            optionalCustomId,
            mappers,
            containerPointer
        }),
        componentDefinition.componentType
    )
}

const notifyExtensionSerializeDataAfter = (
    ps: PS,
    componentPointer: Pointer,
    componentDefinition: CompStructure,
    flags: any,
    pageId: string
) => {
    const {hooks} = ps.extensionAPI as HooksExtensionApi
    hooks.executeHook(
        extensionHooks.COMPONENTS_HOOKS.SERIALIZE.DATA_AFTER.createEvent({
            componentPointer,
            componentDefinition,
            flags,
            pageId
        }),
        componentDefinition.componentType
    )
}

const notifyInnerElementDataUpdateBefore = (ps: PS, dsHooks: DsHooks, componentPointer: CompRef, dataItem: any) => {
    const componentDefinition = ps.dal.get(componentPointer)
    const compType = componentDefinition.componentType
    dsHooks.executeHook(dsHooks.HOOKS.DATA.UPDATE_BEFORE, compType, [ps, componentPointer, dataItem])
}

// ============================================================================
// CATEGORY 1: DM EVENTS → IMPLEMENTATION HOOKS
// ============================================================================
// These bridges listen to Document Manager events and trigger implementation hooks.
// DM events are internal to the Document Manager system.

export function bridgeDMEventsToHooks(ps: PS, documentManager: DocumentManager, dsHooks: DsHooks) {
    const {EVENTS, eventEmitter, experimentInstance} = documentManager
    const undoRedoEventsToTriggerAutoSave = [
        EVENTS.UNDO_REDO.TAKE_SNAPSHOT,
        EVENTS.UNDO_REDO.UNDO,
        EVENTS.UNDO_REDO.REDO
    ]

    const undoRedoEventsToTriggerAfterApplySnapShot = [EVENTS.UNDO_REDO.UNDO, EVENTS.UNDO_REDO.REDO]

    const notifyAutosave = () => dsHooks.executeHook(dsHooks.HOOKS.AUTOSAVE.ACTION, null!, [ps])

    const notifyAfterApplySnapshot = () =>
        dsHooks.executeHook(dsHooks.HOOKS.UNDO_REDO.AFTER_APPLY_SNAPSHOT, null!, [ps])

    undoRedoEventsToTriggerAutoSave.forEach(ev => eventEmitter.on(ev, notifyAutosave))
    undoRedoEventsToTriggerAfterApplySnapShot.forEach(ev => eventEmitter.on(ev, notifyAfterApplySnapshot))

    if (EVENTS.CSAVE) {
        eventEmitter.on(EVENTS.CSAVE.NON_RECOVERABLE_ERROR, () => {
            dsHooks.executeHook(dsHooks.HOOKS.AUTOSAVE.CSAVE_NON_RECOVERABLE_ERROR, null!, [ps])
        })
    }

    eventEmitter.on(EVENTS.ROUTERS.BEFORE_UPDATE, (routerPtr: Pointer, routerData: RoutersDefinition) => {
        dsHooks.executeHook(dsHooks.HOOKS.ROUTER.BEFORE_UPDATE, 'router', [ps, routerPtr, routerData])
    })
    eventEmitter.on(EVENTS.ROUTERS.AFTER_ADD, (routerPtr: Pointer, routerData: RoutersDefinition) => {
        dsHooks.executeHook(dsHooks.HOOKS.ROUTER.AFTER_ADD, 'router', [ps, routerPtr, routerData])
    })

    eventEmitter.on(EVENTS.ROUTERS.BEFORE_REMOVE, (routerPtr: Pointer) => {
        dsHooks.executeHook(dsHooks.HOOKS.ROUTER.BEFORE_REMOVE, 'router', [ps, routerPtr])
    })
    eventEmitter.on(EVENTS.ROUTERS.ON_DISCONNECT, (routerPtr: Pointer, pageId: string) => {
        dsHooks.executeHook(dsHooks.HOOKS.ROUTER.ON_DISCONNECT, 'router', [ps, routerPtr, pageId])
    })
    eventEmitter.on(EVENTS.ROUTERS.AFTER_ADD, (routerPtr: Pointer, routerData: RoutersDefinition) => {
        dsHooks.executeHook(dsHooks.HOOKS.ROUTER.AFTER_ADD, 'router', [ps, routerPtr, routerData])
    })

    eventEmitter.on(EVENTS.COMPONENTS.AFTER_REMOVE, (compPointer: Pointer, compType: string, ...extraArgs: any[]) => {
        dsHooks.executeHook(dsHooks.HOOKS.REMOVE.AFTER!, compType, [ps, compPointer, ...extraArgs])
    })

    if (EVENTS.LOCAL_EDITOR) {
        eventEmitter.on(EVENTS.LOCAL_EDITOR.AFTER_SYNC_FROM_FS, () => {
            dsHooks.executeHook(dsHooks.HOOKS.IMPORT_EXPORT.AFTER_IMPORT, null!, [ps])
        })
    }

    eventEmitter.on(EVENTS.CONNECTIONS.UPDATE_AFTER, (itemToUpdate: any, componentPointer: Pointer) => {
        dsHooks.executeHook(dsHooks.HOOKS.DATA.AFTER_UPDATE_CONNECTIONS, 'updateConnectionsItem', [
            ps,
            itemToUpdate,
            componentPointer
        ])
    })

    // ============================================================================
    // CATEGORY 2: IMPLEMENTATION HOOKS → EXTENSION HOOKS
    // ============================================================================
    // These bridges listen to implementation hooks and trigger extension hooks.
    // All implementation hook names in this category are aligned to match their
    // extension counterparts exactly (no 'impl_' prefix) to enable future auto-bridge.
    //

    dsHooks.registerHook(dsHooks.HOOKS.ADD_ROOT.BEFORE, notifyBeforeAddHookToExtensions)
    dsHooks.registerHook(dsHooks.HOOKS.ADD.AFTER, notifyExtensionsAfterAdd)
    if (experimentInstance.isOpen('dm_moveRootAfterAddComponentHook')) {
        dsHooks.registerHook(dsHooks.HOOKS.ADD_ROOT.AFTER, notifyExtensionsAfterRootAdd)
    }

    dsHooks.registerHook(
        dsHooks.HOOKS.GLOBAL_VARIABLES.WIX_THEME_VAR_WAS_CHANGED,
        notifyGlobalWixThemeVariableValueWasChanged
    )

    dsHooks.registerHook(dsHooks.HOOKS.SERIALIZE.DATA_AFTER, notifyExtensionSerializeDataAfter)

    dsHooks.registerHook(dsHooks.HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE, (componentPointer: CompRef, dataItem: any) => {
        notifyInnerElementDataUpdateBefore(ps, dsHooks, componentPointer, dataItem)
    })

    // ============================================================================
    // CATEGORY 3: EXTENSION HOOKS → IMPLEMENTATION HOOKS
    // ============================================================================
    // These bridges listen to extension hooks and trigger implementation hooks.
    // All implementation hook names in this category are aligned to match their
    // extension counterparts exactly to enable future auto-bridge.

    const {hooks: extensionHooksApi} = ps.extensionAPI as HooksExtensionApi
    extensionHooksApi.registerHookWithExtensionArgs(
        extensionHooks.DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE.id,
        (extensionArgs, event) => {
            const eventData = event as unknown as InnerElementDataUpdateBefore
            notifyInnerElementDataUpdateBefore(ps, dsHooks, eventData.componentPointer, eventData.dataItem)
        }
    )

    extensionHooksApi.registerHookWithExtensionArgs(
        extensionHooks.BUILDER_COMPONENTS_HOOKS.HOT_RELOAD.APP_MANIFEST_UPDATED.id,
        (extensionArgs, {appId}: AppManifestUpdatedEvent) => {
            dsHooks.executeHook(dsHooks.HOOKS.HOT_RELOAD.APP_MANIFEST_UPDATED, undefined, [ps, appId])
        }
    )
}
